import React, { useEffect, useState } from "react";
import { API, apiError, eur, formatDateTime } from "@/lib/api";
import { StatusBadge, StoreBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [errors, setErrors] = useState([]);
  const [stores, setStores] = useState([]);
  const [filter, setFilter] = useState(() => localStorage.getItem("orders_store_filter") || "");
  const [loading, setLoading] = useState(false);

  const load = async (storeId) => {
    setLoading(true);
    try {
      const r = await API.get("/orders", { params: storeId && storeId !== "all" ? { store_id: storeId } : {} });
      setOrders(r.data.orders);
      setErrors(r.data.errors || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    API.get("/stores").then((r) => setStores(r.data.stores)).catch(() => {});
  }, []);

  // Filtro ricordato (localStorage). Se mai scelto, il default e' lo store della sessione attiva.
  useEffect(() => {
    if (filter !== "") return;
    API.get("/session")
      .then((r) => setFilter(r.data?.active && r.data.store ? r.data.store.id : "all"))
      .catch(() => setFilter("all"));
  }, []);

  useEffect(() => {
    if (filter !== "") load(filter);
  }, [filter]);

  return (
    <div data-testid="orders-screen">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Tutti gli ordini</h1>
          <p className="text-sm text-muted-foreground">Ordini di tutti gli store, dal piu recente. Lo store e visibile su ogni riga.</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            data-testid="store-filter-select"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              localStorage.setItem("orders_store_filter", e.target.value);
            }}
            className="h-11 rounded-lg border border-input bg-white px-3 text-sm font-semibold outline-none focus:border-blue-500"
          >
            <option value="all">Tutti gli store</option>
            {stores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.code})
              </option>
            ))}
          </select>
          <button
            data-testid="orders-refresh-btn"
            onClick={() => load(filter)}
            className="flex h-11 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold hover:bg-slate-100"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Aggiorna
          </button>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="mb-4 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-800" data-testid="orders-store-errors">
          {errors.map((e) => (
            <div key={e.store}>Store {e.store}: {e.error}</div>
          ))}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="p-3">Store</th>
              <th className="p-3">Ordine</th>
              <th className="p-3">Data</th>
              <th className="p-3">Cliente</th>
              <th className="p-3">Localita / CAP</th>
              <th className="p-3">Totale</th>
              <th className="p-3">Pagamento</th>
              <th className="p-3">Evasione Shopify</th>
              <th className="p-3">Spedizione</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && !loading && (
              <tr>
                <td colSpan={9} className="p-8 text-center text-muted-foreground" data-testid="orders-empty">
                  Nessun ordine trovato.
                </td>
              </tr>
            )}
            {orders.map((o) => (
              <tr key={`${o.store.id}-${o.shopify_order_id}`} data-testid={`order-row-${o.name}`} className="border-t">
                <td className="p-3">
                  <StoreBadge code={o.store.code} testid={`order-store-${o.store.code}-${o.name}`} />
                </td>
                <td className="p-3 font-mono font-semibold">
                  {o.name}
                  {o.in_session && (
                    <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-blue-700">in sessione</span>
                  )}
                </td>
                <td className="p-3 text-muted-foreground">{formatDateTime(o.created_at)}</td>
                <td className="p-3">{o.customer_name}</td>
                <td className="p-3">
                  {o.city} <span className="font-mono text-muted-foreground">{o.zip}</span>
                </td>
                <td className="p-3 font-mono font-semibold">{eur(o.total_cents)}</td>
                <td className="p-3">
                  {o.payment_gateways?.some((g) => (g || "").toLowerCase().includes("cash on delivery")) ? (
                    <span className="rounded-md border border-orange-300 bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-800">
                      Contrassegno
                    </span>
                  ) : (
                    <span className="rounded-md border border-teal-300 bg-teal-100 px-2 py-0.5 text-xs font-semibold text-teal-800">Pagato</span>
                  )}
                </td>
                <td className="p-3">
                  {o.fulfillment_status === "FULFILLED" ? (
                    <span className="rounded-md border border-teal-300 bg-teal-100 px-2 py-0.5 text-xs font-semibold text-teal-800">Evase</span>
                  ) : (
                    <span className="rounded-md border border-amber-300 bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">Inevaso</span>
                  )}
                </td>
                <td className="p-3">
                  {o.shipment ? (
                    <div>
                      <StatusBadge
                        status={o.shipment.active ? (o.shipment.status === "fulfilled" ? "completed" : o.shipment.status === "created" ? "shipment_created" : "processing") : "cancelled"}
                        testid={`order-shipment-${o.name}`}
                      />
                      {o.shipment.tracking && <div className="mt-1 font-mono text-[11px] text-muted-foreground">{o.shipment.tracking}</div>}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
